import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, tap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { NewMessageAlertService } from '../message/new-message-alert.service';

const RENEWED_TOKEN_HEADER = 'X-New-Token';

/** sessionStorage flag: set before the reload that follows a role change, consumed by AppComponent. */
export const ROLES_CHANGED_NOTICE_KEY = 'rolesChangedNotice';

// 401s from these endpoints are handled locally by the calling dialog (e.g. "wrong password")
// and must not trigger the global "session expired" flow.
const AUTH_ENDPOINTS = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'];

export const authInterceptor: HttpInterceptorFn = (request, next) => {
  const authService = inject(AuthService);
  const newMessageAlerts = inject(NewMessageAlertService);
  const token = authService.getToken();
  const authorizedRequest = token
    ? request.clone({setHeaders: {Authorization: `Bearer ${token}`}})
    : request;

  return next(authorizedRequest).pipe(
    tap((event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse) {
        // New messages have no push channel - the next ordinary request is the trigger (throttled).
        if (token && !request.url.includes('/unread-ids')) {
          newMessageAlerts.checkSoon();
        }
        const renewedToken = event.headers.get(RENEWED_TOKEN_HEADER);
        if (renewedToken && authService.applyRenewedToken(renewedToken)) {
          // Roles changed (see JwtAuthenticationFilter): much of the UI reads roles once when it is built, so
          // start over from the start page - a full reload rebuilds everything from the new token. The notice
          // is shown by AppComponent after the reload.
          try {
            sessionStorage.setItem(ROLES_CHANGED_NOTICE_KEY, '1');
          } catch {
            // sessionStorage unavailable: reload anyway, just without the notice
          }
          window.location.assign('/');
        }
      }
    }),
    catchError((error: unknown) => {
      const isAuthEndpoint = AUTH_ENDPOINTS.some(endpoint => request.url.includes(endpoint));
      if (token && !isAuthEndpoint && error instanceof HttpErrorResponse && error.status === 401) {
        authService.notifySessionExpired();
      }
      return throwError(() => error);
    })
  );
};
